import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { loadLocalPlayers, loadLocalRatings } from '../demoData';
import type { LoadState } from '../loadState';

export interface WelfareFlag {
  id: string;
  player_id: string;
  player_name: string | null;
  kind: 'velocity_drop' | 'psych_social_low';
  corner: 'tech' | 'phys' | 'psych' | 'social' | null;
  delta: number;        // pts dropped (negative) for velocity, or score for psych_social_low
  rating_count: number; // how many ratings the flag is computed from
}

interface RatingRow {
  ratee_id: string;
  corner_id: string;
  score: number;
  created_at: string;
}

interface PlayerRow {
  id: string;
  name: string;
}

const CORNER_KEY: Array<{ key: WelfareFlag['corner']; matches: (id: string) => boolean }> = [
  { key: 'tech',   matches: (id) => /tech|technical/i.test(id) },
  { key: 'phys',   matches: (id) => /phys/i.test(id) },
  { key: 'psych',  matches: (id) => /psych/i.test(id) },
  { key: 'social', matches: (id) => /social/i.test(id) },
];

const VELOCITY_DROP_THRESHOLD = 15;
const PSYCH_SOCIAL_FLOOR = 40;

interface WelfareFlagsResult {
  flags: WelfareFlag[];
  state: LoadState;
  errorMessage?: string;
}

/**
 * Compute welfare flags for a team's players client-side.
 * Two flag kinds, both from the brief §10.4:
 *  - velocity_drop: recent 14-day mean drops more than 15pts vs prior 14
 *  - psych_social_low: recent 14-day mean of (psych + social) / 2 < 40
 *
 * Coach-visible only — never surfaced to player or parent.
 *
 * Returns the shared LoadState contract so Reviews can show "couldn't
 * compute welfare flags" distinctly from "no flags raised". An 'error'
 * state means BOTH the live query AND the demo fallback produced
 * nothing — i.e. we can't tell coach anything trustworthy.
 */
export function useWelfareFlags(teamId: string | undefined): WelfareFlagsResult {
  const [result, setResult] = useState<WelfareFlagsResult>({ flags: [], state: 'loading' });

  useEffect(() => {
    if (!teamId) {
      setResult({ flags: [], state: 'empty' });
      return;
    }
    let cancelled = false;
    setResult({ flags: [], state: 'loading' });

    (async () => {
      let playerRows: PlayerRow[] = [];
      let ratings: RatingRow[] = [];
      let liveError: string | undefined;
      try {
        // Roster lives on team_players joined with users (migration 00008).
        // No standalone `players` table on the live DB.
        const { data: players, error: playerError } = await supabase
          .from('team_players')
          .select('player_id, users!inner(name)')
          .eq('team_id', teamId)
          .eq('is_active', true);
        if (cancelled) return;
        if (playerError) {
          liveError = playerError.message;
        } else {
          playerRows = ((players ?? []) as any[]).map((r) => ({
            id: r.player_id as string,
            name: (r.users?.name as string | undefined) ?? '',
          }));

          if (playerRows.length > 0) {
            const since = new Date();
            since.setDate(since.getDate() - 28);
            const { data: r, error: ratingsError } = await supabase
              .from('ratings')
              .select('ratee_id, corner_id, score, created_at')
              .in('ratee_id', playerRows.map((p) => p.id))
              .gte('created_at', since.toISOString())
              .not('revealed_at', 'is', null);
            if (cancelled) return;
            if (ratingsError) {
              liveError = ratingsError.message;
            } else {
              ratings = (r ?? []) as RatingRow[];
            }
          }
        }
      } catch (err) {
        if (cancelled) return;
        liveError = err instanceof Error ? err.message : 'fetch failed';
      }

      // Demo-data fallback
      if (playerRows.length === 0) {
        playerRows = loadLocalPlayers()
          .filter((p) => p.team_id === teamId)
          .map((p) => ({ id: p.id, name: p.name }));
      }
      if (ratings.length === 0 && playerRows.length > 0) {
        const sinceMs = Date.now() - 28 * 86_400_000;
        const ids = new Set(playerRows.map((p) => p.id));
        ratings = loadLocalRatings()
          .filter((r) => ids.has(r.ratee_id) && new Date(r.created_at).getTime() >= sinceMs);
      }

      if (cancelled) return;

      // If both live AND demo produced nothing AND the live path errored,
      // surface 'error' so the page can show a concrete message.
      if (playerRows.length === 0 && liveError) {
        setResult({ flags: [], state: 'error', errorMessage: liveError });
        return;
      }

      const computed = computeFlags(playerRows, ratings);
      setResult({ flags: computed, state: computed.length > 0 ? 'ok' : 'empty' });
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  return result;
}

export function computeFlags(players: PlayerRow[], ratings: RatingRow[]): WelfareFlag[] {
  const cutoff = Date.now() - 14 * 86_400_000;
  const out: WelfareFlag[] = [];

  for (const p of players) {
    const playerRatings = ratings.filter((r) => r.ratee_id === p.id);
    if (playerRatings.length === 0) continue;

    // velocity_drop per corner
    for (const { key, matches } of CORNER_KEY) {
      const cornerRatings = playerRatings.filter((r) => matches(r.corner_id));
      if (cornerRatings.length < 4) continue;
      const recent = cornerRatings.filter((r) => new Date(r.created_at).getTime() >= cutoff);
      const prior = cornerRatings.filter((r) => new Date(r.created_at).getTime() < cutoff);
      if (recent.length < 2 || prior.length < 2) continue;
      const recentAvg = mean(recent.map((r) => r.score));
      const priorAvg = mean(prior.map((r) => r.score));
      const delta = recentAvg - priorAvg;
      if (delta <= -VELOCITY_DROP_THRESHOLD) {
        out.push({
          id: `${p.id}-velocity-${key}`,
          player_id: p.id,
          player_name: p.name,
          kind: 'velocity_drop',
          corner: key,
          delta: Math.round(delta),
          rating_count: cornerRatings.length,
        });
      }
    }

    // psych_social_low — mean of psych + social over last 14 days
    const psychSocialRecent = playerRatings.filter((r) =>
      (CORNER_KEY[2].matches(r.corner_id) || CORNER_KEY[3].matches(r.corner_id))
      && new Date(r.created_at).getTime() >= cutoff,
    );
    if (psychSocialRecent.length >= 4) {
      const score = mean(psychSocialRecent.map((r) => r.score));
      if (score < PSYCH_SOCIAL_FLOOR) {
        out.push({
          id: `${p.id}-psychsocial`,
          player_id: p.id,
          player_name: p.name,
          kind: 'psych_social_low',
          corner: null,
          delta: Math.round(score),
          rating_count: psychSocialRecent.length,
        });
      }
    }
  }

  return out;
}

function mean(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}
